/**
 * DataHubTabView - Data Hub tab content with performance analytics
 *
 * Passes a pre-built render prop to PerformanceAnalytics so the comparison
 * player flows to IndividualMetricInsights for an orange trend line overlay.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React, { memo } from "react";

import PerformanceAnalytics from "@/features/player-detail/components/organisms/PerformanceAnalytics";

import { Stack } from "@/components/atoms/Stack";
import type {
  PerformanceAnalyticsPlayerScoresFragment,
  PlayerDetailPlayerMetaFragment,
} from "@/generated/graphql";

/**
 * Props for DataHubTabView component
 */
interface DataHubTabViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Player data fragment */
  readonly player?: PlayerDetailPlayerMetaFragment | null;
  /** Whether the screen is mobile sized */
  readonly isSmallScreen: boolean;
  /** Stable render prop for additional content below the performance analytics grid */
  readonly renderAdditionalContent: (
    comparisonPlayer: PerformanceAnalyticsPlayerScoresFragment | null
  ) => React.ReactNode;
}

/**
 * DataHubTabView renders the Data Hub tab with performance analytics
 * @param props - View props
 * @param props.isDark - Whether dark mode is active
 * @param props.player - Player data fragment
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @param props.renderAdditionalContent - Stable render prop for IndividualMetricInsights
 */
const DataHubTabView = ({
  isDark,
  player,
  isSmallScreen,
  renderAdditionalContent,
}: DataHubTabViewProps) => (
  <Stack className={`w-full ${isSmallScreen ? "gap-4 p-3" : "gap-6 p-4"}`}>
    <PerformanceAnalytics
      isDark={isDark}
      player={player}
      isSmallScreen={isSmallScreen}
      renderAdditionalContent={renderAdditionalContent}
    />
  </Stack>
);

DataHubTabView.displayName = "DataHubTabView";

export default memo(DataHubTabView);
